import { FC, useState } from "react";
import { Badge, Collapse, Form, ListGroupItem } from "react-bootstrap";
import { CuttlefishBuild } from "../../../models/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTerminal } from "@fortawesome/free-solid-svg-icons";
import { CopyButton } from "../../buttons/CopyButton";
import { getSizeBadgeVariant } from "../../../common/Helpers";
import styles from "../../modals/CuttlefishBuildModal.module.scss";

interface Props {
    build: CuttlefishBuild;
    toggleBuild: (buildId: number) => void;
    toggleTag: (network: string, tag: string) => void;
    toggleSize: (network: string, size: number) => void;
}

export const CuttlefishNetworkListItem: FC<Props> = ({
    build,
    toggleBuild,
    toggleTag,
    toggleSize
}) => {
    const [showCommand, updateShowCommand] = useState(false);
    return (
        <ListGroupItem
            variant={build.enabled ? "success" : ""}
            className={`py-2 ${!build.enabled ? "opacity-50" : ""}`}
            onClick={() => toggleBuild(build.id)}
        >
            <div className={styles.buildRow}>
                <Form.Check
                    type="checkbox"
                    className="flex-shrink-0"
                    id={`build-toggle-${build.id}`}
                    checked={build.enabled}
                    onChange={() => toggleBuild(build.id)}
                />
                <div
                    className={`${styles.buildName} ${
                        !build.enabled ? "text-muted" : ""
                    }`}
                >
                    <span className="medium-font">{build.name}</span>
                    <FontAwesomeIcon
                        size="xs"
                        title={showCommand ? "Hide command" : "Show command"}
                        className={`ms-3 mouseHover ${
                            showCommand ? "text-primary" : "text-muted"
                        }`}
                        icon={faTerminal}
                        onClick={(event: any) => {
                            event.stopPropagation();
                            updateShowCommand(!showCommand);
                        }}
                    />
                </div>
                <div
                    className={`${styles.buildTags} d-flex gap-1 flex-wrap justify-content-end`}
                >
                    {build.size !== undefined ? (
                        <Badge
                            bg={
                                build.enabled
                                    ? getSizeBadgeVariant(build.size)
                                    : "secondary"
                            }
                            className={`mouseHover text-white ${styles.tagBadge}`}
                            title={`Toggle all ${build.size}MB builds in ${build.network}`}
                            onClick={(event: any) => {
                                event.stopPropagation();
                                toggleSize(build.network, build.size as number);
                            }}
                        >
                            {build.size} MB
                        </Badge>
                    ) : null}
                    {build.tags
                        .filter(
                            tag =>
                                tag.toLowerCase() !==
                                build.network.toLowerCase()
                        )
                        .map(tag => (
                            <Badge
                                key={tag}
                                bg={build.enabled ? "primary" : "secondary"}
                                className={`mouseHover ${styles.tagBadge}`}
                                title={`Toggle all "${tag}" builds in ${build.network}`}
                                onClick={(event: any) => {
                                    event.stopPropagation();
                                    toggleTag(build.network, tag);
                                }}
                            >
                                {tag}
                            </Badge>
                        ))}
                </div>
            </div>
            <Collapse in={showCommand}>
                <div>
                    <div
                        className="d-flex align-items-start gap-2 mt-2 px-2 border-start subtle-border"
                        onClick={(event: any) => {
                            event.stopPropagation();
                        }}
                    >
                        <code
                            className={`flex-grow-1 text-muted ${styles.commandText}`}
                        >
                            {build.command}
                        </code>
                        <CopyButton value={build.command} />
                    </div>
                </div>
            </Collapse>
        </ListGroupItem>
    );
};
